/**
 * Devices Status Card
 * 
 * Shows status of all devices in the household with:
 * - Pairing status
 * - Online/Offline status
 * - Call status (if in call)
 * - Last active time
 * - Device UUID
 * - Actions menu (Rename, Unpair, Delete)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  normalizeDeviceStatus,
  formatLastActive, 
  formatDeviceUUID,
  getDeviceIcon,
  isPaired as isDevicePaired
} from '../../../utils/deviceStatus';
import { unpairDevice, updateDevice, deleteDevice } from '../../../api/deviceApi';
import axiosClient from '../../../api/axiosClient';

function DevicesStatusCard({ devices, seniors, isAdmin, currentCall, onDeviceUpdated }) {
  const navigate = useNavigate();
  const [openMenuId, setOpenMenuId] = useState(null);
  const [toast, setToast] = useState(null);

  const getSeniorName = (seniorId) => {
    const senior = seniors?.find(s => s.id === seniorId);
    return senior?.name || 'Unbekannt';
  };

  // Check if device is in an active call
  const isDeviceInCall = (deviceId) => {
    return currentCall && 
           (currentCall.status === 'ringing' || currentCall.status === 'in_call') &&
           currentCall.deviceId === deviceId;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isMenuButton = event.target.closest('button[title="Aktionen"]');
      const isMenuContent = event.target.closest('.device-action-menu');
      
      if (!isMenuButton && !isMenuContent && openMenuId) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openMenuId]);

  // Toast Helper
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Action Handlers
  const handleUnpair = async (device) => {
    if (!device.device_uuid) {
      showToast('Dieses Gerät kann nicht entkoppelt werden (keine UUID)', 'error');
      return;
    }

    if (!window.confirm(`Möchten Sie das Gerät "${device.device_name || device.name || 'Unbenannt'}" wirklich entkoppeln?`)) {
      return;
    }

    try {
      await unpairDevice(device.device_uuid);
      showToast('Gerät erfolgreich entkoppelt');
      setOpenMenuId(null);
      if (onDeviceUpdated) {
        onDeviceUpdated();
      }
    } catch (error) {
      showToast(error.message || 'Fehler beim Entkoppeln', 'error');
    }
  };

  const handleDelete = async (device) => {
    if (!window.confirm(`Möchten Sie das Gerät "${device.device_name || device.name || 'Unbenannt'}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      return;
    }

    try {
      await deleteDevice(device.id);
      showToast('Gerät erfolgreich gelöscht');
      setOpenMenuId(null);
      if (onDeviceUpdated) {
        onDeviceUpdated();
      }
    } catch (error) {
      showToast(error.message || 'Fehler beim Löschen', 'error');
    }
  };

  const handleCleanupInconsistent = async (device) => {
    const deviceName = device.device_name || device.name || 'Unbenannt';
    
    // Show options to user
    const action = window.confirm(
      `Inkonsistentes Gerät "${deviceName}" bereinigen:\n\n` +
      `Klicken Sie OK, um das Gerät vollständig zu löschen.\n` +
      `Klicken Sie Abbrechen, um nur die Zuordnung zu entfernen (Gerät bleibt registriert).`
    );

    try {
      if (action) {
        // Option A: Vollständig löschen
        await deleteDevice(device.id);
        showToast('Inkonsistentes Gerät wurde gelöscht');
      } else {
        // Option B: Nur Zuordnungen entfernen (Status auf unpaired setzen)
        // Wir müssen das Gerät aktualisieren: senior_id = null, household_id = null, status = unpaired
        await axiosClient.patch(`/api/devices/${device.id}`, {
          senior_id: null,
          household_id: null,
          status: 'unpaired'
        });
        showToast('Gerätezuordnung wurde entfernt');
      }
      
      setOpenMenuId(null);
      if (onDeviceUpdated) {
        onDeviceUpdated();
      }
    } catch (error) {
      console.error('Error cleaning up inconsistent device:', error);
      showToast(error.response?.data?.detail || error.message || 'Fehler beim Bereinigen', 'error');
    }
  };

  if (!devices || devices.length === 0) {
    return (
      <div className="bg-neutral-900/80 backdrop-blur-xl rounded-2xl border border-neutral-700/50 p-6">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-2xl">📱</span>
          <h3 className="text-lg font-bold text-white">Geräte</h3>
        </div>
        
        <div className="text-center py-8">
          <p className="text-neutral-400 mb-4">Noch keine Geräte verbunden</p>
          {isAdmin && (
            <button
              onClick={() => navigate('/family/admin/devices/wizard')}
              className="px-4 py-2 bg-gradient-to-r from-[#6DCEE8] to-[#5B4B8A] text-white rounded-xl text-sm hover:shadow-lg hover:shadow-[#6DCEE8]/50 transition-all"
            >
              Gerät hinzufügen
            </button>
          )}
        </div>
      </div>
    );
  }

  // Count devices by connection state
  const onlineCount = devices.filter(d => d.connection_state === 'online').length;
  const offlineCount = devices.length - onlineCount;

  return (
    <div className="bg-neutral-900/80 backdrop-blur-xl rounded-2xl border border-neutral-700/50 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-2xl">📱</span>
          <h3 className="text-lg font-bold text-white">Geräte</h3>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-xs text-green-400 font-medium">{onlineCount} online</span>
          <span className="text-xs text-neutral-500">•</span>
          <span className="text-xs text-neutral-400 font-medium">{offlineCount} offline</span>
        </div>
      </div>

      {/* Devices List */}
      <div className="space-y-3 mb-4">
        {devices.map((device, idx) => {
          const inCall = isDeviceInCall(device.id);
          const statusInfo = normalizeDeviceStatus(device, inCall);
          const deviceName = device.device_name || device.name || 'Unbenanntes Gerät';
          const isPaired = isDevicePaired(device);
          
          return (
            <div
              key={device.id || idx}
              className="bg-neutral-800/50 rounded-xl p-4 hover:bg-neutral-800/70 transition-colors"
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl">{getDeviceIcon(device.type)}</span>
                
                <div className="flex-1 min-w-0">
                  {/* Device Name & Status Badge */}
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="text-sm font-semibold text-white truncate">
                      {deviceName}
                    </h4>
                    {/* Main Status Badge */}
                    <span 
                      className={`text-xs px-2 py-0.5 rounded-full border ${statusInfo.badge} flex items-center gap-1`}
                      title={statusInfo.tooltip}
                    >
                      <span>{statusInfo.emoji}</span>
                      <span>{statusInfo.label}</span>
                    </span>
                  </div>
                  
                  {/* Inconsistency Warning */}
                  {statusInfo.isInconsistent && (
                    <div className="mb-2 p-2 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                      <p className="text-xs text-orange-300">
                        ⚠️ Dieses Gerät ist einem Senior zugeordnet, aber nicht korrekt gekoppelt. Bitte bereinigen.
                      </p>
                    </div>
                  )}
                  
                  {/* Senior Assignment */}
                  {device.senior_id ? (
                    <p className="text-xs text-neutral-400 mb-1">
                      Zugeordnet: <span className="text-[#6DCEE8]">{getSeniorName(device.senior_id)}</span>
                    </p>
                  ) : (
                    <p className="text-xs text-neutral-500 mb-1">Noch nicht zugewiesen</p>
                  )}
                  
                  {/* Device UUID */}
                  {device.device_uuid && (
                    <p className="text-xs text-neutral-500 font-mono">
                      UUID: {formatDeviceUUID(device.device_uuid)}
                    </p>
                  )}
                  
                  {/* Last Active */}
                  {device.last_active && (
                    <p className="text-xs text-neutral-500 mt-1">
                      Zuletzt aktiv: {formatLastActive(device.last_active)}
                    </p>
                  )}
                  
                  {/* Location (if available) */}
                  {device.location && (
                    <p className="text-xs text-neutral-500 mt-1">📍 {device.location}</p>
                  )}
                </div>

                {/* Actions Menu (Admin only) */}
                {isAdmin && (
                  <div className="relative">
                    <button
                      onClick={() => setOpenMenuId(openMenuId === device.id ? null : device.id)}
                      className="p-2 text-neutral-400 hover:text-white hover:bg-neutral-700/50 rounded-lg transition-all"
                      title="Aktionen"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                      </svg>
                    </button>

                    {/* Dropdown Menu */}
                    {openMenuId === device.id && (
                      <div className="device-action-menu absolute right-0 mt-2 w-56 bg-neutral-800 border border-neutral-700 rounded-lg shadow-2xl z-10 py-1">
                        {/* Cleanup Inconsistent (only for inconsistent devices) */}
                        {statusInfo.isInconsistent && (
                          <>
                            <button
                              onClick={() => handleCleanupInconsistent(device)}
                              className="w-full px-4 py-2 text-left text-sm text-orange-400 hover:bg-orange-500/10 transition-colors flex items-center gap-2"
                            >
                              <span>🔧</span>
                              <span>Inkonsistenz bereinigen</span>
                            </button>
                            <div className="border-t border-neutral-700 my-1"></div>
                          </>
                        )}

                        {/* Unpair (only for properly paired devices) */}
                        {isPaired && !statusInfo.isInconsistent && (
                          <button
                            onClick={() => handleUnpair(device)}
                            className="w-full px-4 py-2 text-left text-sm text-orange-400 hover:bg-orange-500/10 transition-colors flex items-center gap-2"
                          >
                            <span>🔓</span>
                            <span>Entkoppeln</span>
                          </button>
                        )}

                        {/* Delete (Admin only) */}
                        <button
                          onClick={() => handleDelete(device)}
                          className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-2"
                        >
                          <span>🗑️</span>
                          <span>Löschen</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Admin Actions */}
      {isAdmin && (
        <button
          onClick={() => navigate('/family/admin/settings')}
          className="w-full px-4 py-2 bg-neutral-700 text-white rounded-xl hover:bg-neutral-600 transition-colors text-sm font-medium"
        >
          Alle Geräte verwalten
        </button>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-slide-up">
          <div className={`px-6 py-3 rounded-lg shadow-2xl border ${
            toast.type === 'success' 
              ? 'bg-green-500/20 border-green-500/40 text-green-300' 
              : 'bg-red-500/20 border-red-500/40 text-red-300'
          }`}>
            <div className="flex items-center gap-2">
              <span>{toast.type === 'success' ? '✅' : '❌'}</span>
              <span className="text-sm font-medium">{toast.message}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DevicesStatusCard;
